import { useState, useEffect } from "react";
import { CamposDeDesenlace, type Desenlace } from "@/features/almacen/components/CamposDeDesenlace";
import { ArrowLeft, Loader2 } from "lucide-react";
import toast from "react-hot-toast";
import { Badge, type ColorDeBadge } from "@/shared/components/Badge";
import PhotoViewerModal from '@/features/almacen/components/PhotoViewerModal';
import { apiService } from '@/services/core/api.service';
import { usePermissions } from '@/shared/hooks/usePermissions';
import { ALMACEN } from '@/constants/permissions';
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton';

// ─── Types ────────────────────────────────────────────────────────────────────

type EstadoReporte = "Falla/daño" | "Faltante";
type EstatusReporte = "Abierto" | "Cerrado";

export interface Reporte {
  id: string;
  folio: string;
  toolId: string;
  herramienta: string;
  codigoSerie: string;
  internalCode: string;
  proyecto: string;
  responsable: string;
  fechaHora: string;
  createdAtISO: string;
  estado: EstadoReporte;
  estatus: EstatusReporte;
  descripcion: string;
  resolucion: string;
  cerradoPor?: string;
  fechaCierre?: string;
  // ISO crudo del cierre — fechaCierre viene ya recortada para mostrarse, y el
  // tiempo de resolución necesita la fecha completa para restarla.
  closedAtISO?: string;
  fotos: string[];
}

interface Props {
  reporte: Reporte;
  onBack: () => void;
  onCerrar: (id: string, comentario: string, desenlace: Desenlace) => Promise<void>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const Field = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    <p className="text-xs text-gray-500 mb-0.5">{label}</p>
    <div className="text-sm text-gray-800">{children}</div>
  </div>
);

// Mismo estilo de sub-sección que el detalle de herramienta
// (HerramientaForm · GroupTitle): label en mayúsculas + línea a la derecha.
const SECTION_TITLE_STYLE: React.CSSProperties = {
  fontFamily: "'Albert Sans', sans-serif",
  fontWeight: 500,
  fontSize: 12,
  lineHeight: "100%",
  letterSpacing: 0,
  textTransform: "uppercase",
  color: "#979797",
};

const GroupTitle = ({ children, borderless }: { children: React.ReactNode; borderless?: boolean }) => (
  <div className="flex items-center gap-3 mb-4">
    <p className="whitespace-nowrap" style={SECTION_TITLE_STYLE}>
      {children}
    </p>
    {!borderless && <div className="flex-1 border-t" style={{ borderColor: "#E0E0E0" }} />}
  </div>
);

// Cuánto tardó en resolverse, de la creación al cierre.
const tiempoResolucion = (desdeISO?: string, hastaISO?: string) => {
  if (!desdeISO || !hastaISO) return null;
  const ms = new Date(hastaISO).getTime() - new Date(desdeISO).getTime();
  if (!isFinite(ms) || ms < 0) return null;
  const min = Math.round(ms / 60000);
  if (min < 1) return "Menos de 1 minuto";
  if (min < 60) return `${min} minuto${min === 1 ? "" : "s"}`;
  const horas = Math.floor(min / 60);
  const resto = min % 60;
  if (horas < 24) {
    return resto ? `${horas} h ${resto} min` : `${horas} hora${horas === 1 ? "" : "s"}`;
  }
  const dias = Math.floor(horas / 24);
  const horasResto = horas % 24;
  return horasResto ? `${dias} d ${horasResto} h` : `${dias} día${dias === 1 ? "" : "s"}`;
};

const EstadoBadge = ({ estado }: { estado: EstadoReporte }) => {
  const colores: Record<EstadoReporte, ColorDeBadge> = {
    "Falla/daño": "rojo",
    Faltante: "amarillo",
  };
  return <Badge color={colores[estado]}>{estado}</Badge>;
};

// Indicador de estatus junto al título. Mismos colores que el listado de
// incidencias (ReportesActivos); el punto sigue al color del texto.
const ESTATUS_COLOR: Record<EstatusReporte, ColorDeBadge> = {
  Abierto: "azul",
  Cerrado: "gris",
};

const EstatusBadge = ({ estatus }: { estatus: EstatusReporte }) => (
  <Badge color={ESTATUS_COLOR[estatus]} dot>
    {estatus}
  </Badge>
);

// ─── User avatar placeholder ──────────────────────────────────────────────────


// ─── Main component ───────────────────────────────────────────────────────────

const ReporteDetailView = ({ reporte, onBack, onCerrar }: Props) => {
  const [comentario, setComentario] = useState("");
  // Desenlace de la herramienta. Nulo mientras falte algo por elegir, y con eso
  // se bloquea el boton de cerrar: el criterio de ALM-01 es que no se pueda
  // cerrar dejando la herramienta en un estado que nadie decidio.
  const [desenlace, setDesenlace] = useState<Desenlace | null>(null);
  const [subiendoEvidencia, setSubiendoEvidencia] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // Último usuario que tuvo la herramienta asignada antes de la incidencia:
  // el último movimiento de asignación/reasignación previo a su fecha de creación.
  const [ultimoAsignado, setUltimoAsignado] = useState<string | null>(null);
  const [loadingAsignado, setLoadingAsignado] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoadingAsignado(true);
    setUltimoAsignado(null);
    apiService
      .request<{
        data: { type: string; responsibleUserName: string | null; createdAt: string }[];
      }>(`/almacen/herramientas/${reporte.toolId}/movimientos?limit=100`)
      .then((res) => {
        if (cancelled) return;
        const incidenteTs = new Date(reporte.createdAtISO).getTime();
        const previo = (res.data ?? [])
          .filter(
            (m) =>
              (m.type === "assignment" || m.type === "reassignment") &&
              new Date(m.createdAt).getTime() <= incidenteTs,
          )
          .sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )[0];
        setUltimoAsignado(previo?.responsibleUserName ?? null);
      })
      .catch(() => {
        if (!cancelled) setUltimoAsignado(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingAsignado(false);
      });
    return () => {
      cancelled = true;
    };
  }, [reporte.toolId, reporte.createdAtISO]);

  const { hasPermission, isLoading: permsLoading } = usePermissions();
  const canCerrar = !permsLoading && hasPermission(ALMACEN.REPORTES_CERRAR);

  const isClosed = reporte.estatus === "Cerrado";

  const handleCerrar = async () => {
    if (saving || isClosed) return;
    setSaving(true);
    try {
      if (!desenlace) return;
      await onCerrar(reporte.id, comentario, desenlace);
    } catch (err: unknown) {
      const e = err as { statusCode?: number; message?: string | string[] };
      const msg =
        e.statusCode === 500
          ? "Error del servidor. Inténtalo de nuevo."
          : Array.isArray(e.message)
            ? e.message.join(" ")
            : (e.message ?? "Error al cerrar el reporte");
      toast.error(msg);
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto px-8 py-8 max-w-5xl">
      {/* Visor de fotos ampliado */}
      <PhotoViewerModal
        open={viewerIndex !== null}
        onClose={() => setViewerIndex(null)}
        toolPhotos={[]}
        evidencePhotos={reporte.fotos}
        initialTab="evidencia"
        initialIndex={viewerIndex ?? 0}
      />

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={onBack}
            className={estiloDeBoton("secundario")}
          >
            <ArrowLeft className="w-4 h-4" />
            Volver
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              Reporte {reporte.folio || "sin número"}
            </h1>
            <p className="text-sm text-gray-500">
              {isClosed
                ? "Consulta el detalle de esta incidencia y cómo fue resuelta"
                : "Consulta el detalle de esta incidencia"}
            </p>
          </div>
        </div>
        {/* El estatus sube al nivel del título; ya no vive en la tarjeta. */}
        <EstatusBadge estatus={reporte.estatus} />
      </div>

      {/* ── Información general ── */}
      <div
        className="bg-white rounded-xl shadow-sm mb-5"
        style={{ border: "1px solid #E0E0E0" }}
      >
        <div
          className="px-6 pt-5 pb-4"
          style={{ borderBottom: "1px solid #E0E0E0" }}
        >
          <h2 className="text-base font-semibold text-gray-800">
            Información general
          </h2>
        </div>

        <div className="px-6 py-5">
          {/* ── Incidencia ── */}
          <GroupTitle borderless>Incidencia</GroupTitle>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6">
            <Field label="Reporte">
              <span className="font-medium">
                {reporte.folio || "Sin número"}
              </span>
            </Field>
            <Field label="Tipo">
              <EstadoBadge estado={reporte.estado} />
            </Field>
            <Field label="Reportado por">
              <span className="font-medium">{reporte.responsable}</span>
            </Field>
            <Field label="Fecha y hora">
              <span className="font-medium">
                {reporte.fechaHora.replace("/", "-").replace("/", "-")}
              </span>
            </Field>
          </div>

          {/* Descripción del problema (siempre visible). En cerradas se titula
              "Problema reportado" para contrastar con la resolución. */}
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-0.5">
              {isClosed ? "Problema reportado" : "Descripción"}
            </p>
            <p className="text-sm text-gray-800">
              {reporte.descripcion || (
                <span className="text-gray-400 italic">Sin descripción</span>
              )}
            </p>
          </div>

          {/* ── Herramienta ── */}
          <div className="mt-6">
            <GroupTitle>Herramienta</GroupTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6">
              <Field label="Nombre">
                <span className="font-medium">{reporte.herramienta}</span>
              </Field>
              <Field label="Código interno">
                <span className="font-medium">{reporte.internalCode}</span>
              </Field>
              <Field label="Número de serie">
                <span className="font-medium">{reporte.codigoSerie}</span>
              </Field>
              <Field label="Proyecto">
                <span className="font-medium">{reporte.proyecto}</span>
              </Field>
            </div>
          </div>

          {/* ── Asignación actual ── */}
          <div className="mt-6">
            <GroupTitle>Asignación actual</GroupTitle>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-4 gap-x-6">
              {isClosed && (
                <>
                  <Field label="Cerrado por">
                    <span className="font-medium">
                      {reporte.cerradoPor || (
                        <span className="text-gray-400 italic font-normal">
                          Sin registro
                        </span>
                      )}
                    </span>
                  </Field>
                  <Field label="Fecha de cierre">
                    <span className="font-medium">
                      {reporte.fechaCierre || (
                        <span className="text-gray-400 italic font-normal">
                          Sin registro
                        </span>
                      )}
                    </span>
                  </Field>
                </>
              )}
              <Field label="Último usuario asignado">
                <span className="font-medium">
                  {loadingAsignado
                    ? "Cargando…"
                    : (ultimoAsignado ?? "Sin registro")}
                </span>
              </Field>
              {isClosed && (
                <Field label="Tiempo de resolución">
                  <span className="font-medium">
                    {tiempoResolucion(reporte.createdAtISO, reporte.closedAtISO) ?? (
                      <span className="text-gray-400 italic font-normal">
                        Sin registro
                      </span>
                    )}
                  </span>
                </Field>
              )}
            </div>

            {isClosed && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-0.5">
                  Notas de resolución
                </p>
                <p className="text-sm text-gray-800">
                  {reporte.resolucion || (
                    <span className="text-gray-400 italic">
                      Sin comentario de resolución
                    </span>
                  )}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Fotos ── */}
      <div
        className="bg-white rounded-xl shadow-sm mb-5"
        style={{ border: "1px solid #E0E0E0" }}
      >
        <div
          className="px-6 pt-5 pb-4"
          style={{ borderBottom: "1px solid #E0E0E0" }}
        >
          <h2 className="text-base font-semibold text-gray-800">Fotos</h2>
        </div>
        <div className="px-6 py-5">
          {reporte.fotos.length === 0 ? (
            <p className="text-sm text-gray-400 italic">Sin fotos adjuntas</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {reporte.fotos.map((src, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setViewerIndex(i)}
                  className="w-36 h-28 rounded-lg border border-gray-200 overflow-hidden flex items-center justify-center bg-gray-50 cursor-pointer hover:border-[#477FFA] transition-colors"
                >
                  <img
                    src={src}
                    alt={`Foto ${i + 1}`}
                    className="object-contain w-full h-full p-2"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Comentario de resolución y acciones — solo si el reporte está abierto y
            el usuario tiene permiso de cierre (almacen.gestionar.reportes.cerrar) ── */}
      {!isClosed && canCerrar && (
        <>
          <div
            className="bg-white rounded-xl shadow-sm mb-8"
            style={{ border: "1px solid #E0E0E0" }}
          >
            <div
              className="px-6 pt-5 pb-4"
              style={{ borderBottom: "1px solid #E0E0E0" }}
            >
              <h2 className="text-base font-semibold text-gray-800">
                Comentario de resolución
              </h2>
            </div>
            <div className="px-6 py-5">
              <label className="block text-sm text-gray-600 mb-2">
                Agregar comentario
              </label>
              <textarea
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                placeholder="Agrega aquí tu comentario"
                rows={4}
                className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm text-gray-700 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-[#3B5998]"
              />

              <div className="mt-5">
                <CamposDeDesenlace
                  onChange={setDesenlace}
                  onSubiendoChange={setSubiendoEvidencia}
                  esFaltante={reporte.estado === "Faltante"}
                />
              </div>
            </div>
          </div>

          {/* ── Footer actions ── */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onBack}
              className={estiloDeBoton("secundario")}
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleCerrar}
              // Sin desenlace elegido no se puede cerrar: es el criterio de
              // ALM-01. Y con una evidencia a medio subir tampoco, o se
              // guardaria una URL local que el servidor no puede leer.
              disabled={saving || !desenlace || subiendoEvidencia}
              className={estiloDeBoton("primario")}
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Cerrar reporte
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ReporteDetailView;
